import { Box, Progress, Table, Tbody, Td, Text, Thead, Tr, VStack } from "@chakra-ui/react";
import type { LocalDateTime } from "@js-joda/core";
import type { ResponseOf } from "../../../../../core/api";
import { LocalDate } from "@js-joda/core";
import React from "react";
import type { CaregiverId } from "../../../../../shared/schema/schema";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import EntityModalButton, {
  EntityModalButtonGroup,
} from "../../../../entityProfileModal/components/EntityModalButton";
import EntityProfileDataTable from "../../../../entityProfileModal/components/EntityProfileDataTable";
import {
  EntityProfileErrorState,
  EntityProfileLoadingState,
} from "../../../../entityProfileModal/components/EntityProfileFeedback";
import { entityProfileModalColors } from "../../../../entityProfileModal/entityProfileModal.theme";
import { formatCaregiverProfileValue } from "../../../caregiverProfileModal.utils";
import { useCaregiverAttendance } from "./CaregiverProfileAttendanceSection.hooks";

type Props = {
  caregiverId: CaregiverId;
};

type CaregiverAttendanceResponse = ResponseOf<"get", "./caregivers/:caregiverId/attendance">;
type MissedClockInOutApiRow = CaregiverAttendanceResponse["missedClockInOuts"][number];

const attendanceTableSx = {
  "td, th": {
    fontSize: "13px",
    px: "8px",
  },
  th: {
    fontWeight: 700,
    textAlign: "left",
  },
  td: {
    color: entityProfileModalColors.bodyText,
    py: "9px",
  },
};

function formatDate(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (typeof value === "string") {
    try {
      return dateFormatter.toDate(LocalDate.parse(value.split("T")[0]));
    } catch {
      return value;
    }
  }

  try {
    return dateFormatter.toDate(value as LocalDateTime);
  } catch {
    return String(value);
  }
}

function formatTime(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  try {
    return dateFormatter.toTime(value as LocalDateTime);
  } catch {
    return "";
  }
}

function formatMinutes(minutes: number | null | undefined) {
  if (minutes === null || minutes === undefined) {
    return "";
  }

  const hours = Math.trunc(minutes / 60);
  const remainingMinutes = Math.abs(minutes % 60);

  return `${hours}:${String(remainingMinutes).padStart(2, "0")}`;
}

function getSortValue(value: unknown) {
  return value === null || value === undefined ? "" : String(value);
}

function AttendanceSectionCard(props: { children: React.ReactNode; title: string }) {
  return (
    <Box
      bg={entityProfileModalColors.surface}
      borderRadius="16px"
      boxShadow="4px 10px 10px 10px rgba(0, 0, 0, 0.05)"
      m={{ base: "20px 0", md: "20px" }}
      minH="218px"
      pb="60px"
      pt="20px"
      px={{ base: "18px", md: "30px" }}
    >
      <Text color="black" fontSize="18px" fontWeight={700} mb="8px">
        {props.title}
      </Text>
      {props.children}
    </Box>
  );
}

function AttendanceSubheading(props: { children: React.ReactNode }) {
  return (
    <Text
      color={entityProfileModalColors.bodyText}
      fontSize="15px"
      fontWeight={700}
      mb="4px"
      mt="8px"
    >
      {props.children}
    </Text>
  );
}

function AttendanceOverallTable(props: {
  columns: Array<{ key: string; label: string; value: React.ReactNode }>;
}) {
  return (
    <Table size="sm" sx={{ tableLayout: "fixed" }} variant="simple">
      <Thead>
        <Tr>
          {props.columns.map((column) => (
            <Td
              key={column.key}
              borderColor="#ddd"
              color={entityProfileModalColors.bodyText}
              fontSize="13px"
              fontWeight={400}
              px="8px"
              py="6px"
            >
              {column.label}
            </Td>
          ))}
        </Tr>
      </Thead>
      <Tbody>
        <Tr bg="#f9f9f9">
          {props.columns.map((column) => (
            <Td
              key={column.key}
              borderColor="#eee"
              color={entityProfileModalColors.bodyText}
              fontSize="13px"
              px="8px"
              py="9px"
            >
              {column.value}
            </Td>
          ))}
        </Tr>
      </Tbody>
    </Table>
  );
}

function YearControl(props: { currentYear: number; onChange: (year: number) => void }) {
  return (
    <EntityModalButtonGroup style={{ marginBottom: 20 }}>
      <EntityModalButton
        aria-label="Previous year"
        iconClassName="fa fa-chevron-left"
        kind="white"
        title="Previous year"
        onClick={() => props.onChange(props.currentYear - 1)}
      />
      <EntityModalButton kind="white" style={{ cursor: "default", minWidth: 64 }}>
        {props.currentYear}
      </EntityModalButton>
      <EntityModalButton
        aria-label="Next year"
        iconClassName="fa fa-chevron-right"
        kind="white"
        title="Next year"
        onClick={() => props.onChange(props.currentYear + 1)}
      />
    </EntityModalButtonGroup>
  );
}

function getMissedClockRows(rows: MissedClockInOutApiRow[]) {
  return rows.map((row) => ({
    ...row,
    scheduledDateTime: row.missedType === "IN" ? row.endTime : row.startTime,
  }));
}

export default function CaregiverProfileAttendanceSection(props: Props) {
  const currentYear = LocalDate.now().year();
  const [year, setYear] = React.useState(currentYear);
  const attendanceQuery = useCaregiverAttendance(props.caregiverId, year);
  const [lastAttendance, setLastAttendance] = React.useState<typeof attendanceQuery.data>();
  const attendance = attendanceQuery.data ?? lastAttendance;

  React.useEffect(() => {
    if (attendanceQuery.data !== undefined) {
      setLastAttendance(attendanceQuery.data);
    }
  }, [attendanceQuery.data]);
  const missedClockRows = React.useMemo(
    () => getMissedClockRows(attendance?.missedClockInOuts ?? []),
    [attendance?.missedClockInOuts]
  );
  const missedClockOverall = React.useMemo(
    () =>
      missedClockRows.reduce(
        (overall, row) => ({
          missedClockIns: overall.missedClockIns + (row.missedType === "IN" ? 1 : 0),
          missedClockOuts: overall.missedClockOuts + (row.missedType === "OUT" ? 1 : 0),
        }),
        { missedClockIns: 0, missedClockOuts: 0 }
      ),
    [missedClockRows]
  );
  const missedVisits = React.useMemo(
    () => attendance?.missedVisits ?? [],
    [attendance?.missedVisits]
  );
  const missedVisitsOverall = React.useMemo(
    () =>
      missedVisits.reduce(
        (overall, visit) => ({
          totalDurationMinutes: overall.totalDurationMinutes + visit.durationMinutes,
          totalVisitsAmount: overall.totalVisitsAmount + 1,
        }),
        { totalDurationMinutes: 0, totalVisitsAmount: 0 }
      ),
    [missedVisits]
  );
  const caregiverPtosOverall = attendance?.caregiverPtos.overall ?? [];
  const caregiverPtosBreakdown = attendance?.caregiverPtos.breakdown ?? [];

  const content =
    attendance === undefined ? (
      attendanceQuery.isLoading ? (
        <EntityProfileLoadingState label="Loading caregiver attendance" />
      ) : null
    ) : (
      <VStack alignItems="stretch" spacing="20px">
        <AttendanceSectionCard title="Missed Clock in/out">
          <AttendanceSubheading>Overall</AttendanceSubheading>
          <AttendanceOverallTable
            columns={[
              {
                label: "Overall Missed clock ins (instances)",
                key: "missedClockIns",
                value: missedClockOverall.missedClockIns,
              },
              {
                label: "Overall Missed clock outs (instances)",
                key: "missedClockOuts",
                value: missedClockOverall.missedClockOuts,
              },
            ]}
          />

          {missedClockRows.length > 0 ? (
            <>
              <AttendanceSubheading>Breakdown</AttendanceSubheading>
              <EntityProfileDataTable
                columns={[
                  {
                    header: "Date",
                    key: "date",
                    render: (row) => formatDate(row.scheduledDateTime),
                    sortKey: "date",
                    sortValue: (row) => String(row.scheduledDateTime),
                  },
                  {
                    header: "Missed Type",
                    key: "missedType",
                    render: (row) => row.missedType,
                    sortKey: "missedType",
                  },
                  {
                    header: "Scheduled Hour",
                    key: "scheduledHour",
                    render: (row) => formatTime(row.scheduledDateTime),
                    sortKey: "scheduledHour",
                    sortValue: (row) => String(row.scheduledDateTime),
                  },
                ]}
                defaultPageSize={10}
                getRowKey={(row) => `${row.visitInstanceId}-${row.missedType}`}
                pageSizeOptions={[10, 25, 50, 100]}
                rows={missedClockRows}
                tableSx={attendanceTableSx}
              />
            </>
          ) : null}
        </AttendanceSectionCard>

        <AttendanceSectionCard title="Missed Visits">
          <AttendanceSubheading>Overall</AttendanceSubheading>
          <AttendanceOverallTable
            columns={[
              {
                label: "Overall Missed (visits)",
                key: "totalVisitsAmount",
                value: missedVisitsOverall.totalVisitsAmount,
              },
              {
                label: "Overall Missed (hours)",
                key: "totalDurationMinutes",
                value: formatMinutes(missedVisitsOverall.totalDurationMinutes),
              },
            ]}
          />

          {missedVisits.length > 0 ? (
            <>
              <AttendanceSubheading>Breakdown</AttendanceSubheading>
              <EntityProfileDataTable
                columns={[
                  {
                    header: "Date",
                    key: "date",
                    render: (row) => formatDate(row.startTime),
                    sortKey: "date",
                    sortValue: (row) => String(row.startTime),
                  },
                  {
                    header: "Missed Hours",
                    key: "durationMinutes",
                    render: (row) => formatMinutes(row.durationMinutes),
                    sortKey: "durationMinutes",
                  },
                  {
                    header: "Missed Type",
                    key: "missedType",
                    render: (row) => formatCaregiverProfileValue(row.missedNotePredefinedAnswer),
                    sortKey: "missedType",
                  },
                  {
                    header: "Missed Note",
                    key: "missedNote",
                    render: (row) => formatCaregiverProfileValue(row.missedNoteText),
                    sortKey: "missedNote",
                  },
                ]}
                defaultPageSize={10}
                getRowKey={(row) => row.visitInstanceId}
                pageSizeOptions={[10, 25, 50, 100]}
                rows={missedVisits}
                tableSx={attendanceTableSx}
              />
            </>
          ) : null}
        </AttendanceSectionCard>

        <AttendanceSectionCard title="Caregiver's PTO">
          <AttendanceSubheading>Overall</AttendanceSubheading>
          <EntityProfileDataTable
            columns={[
              {
                header: "Label",
                key: "label",
                render: (row) => row.labelText,
                sortKey: "label",
              },
              {
                header: "Fixed / Accumulating",
                key: "ptoSettingType",
                render: (row) => row.ptoSettingType,
                sortKey: "ptoSettingType",
              },
              {
                header: "Overall (hours)",
                key: "overallMinutes",
                render: (row) => formatMinutes(row.overallMinutes),
                sortKey: "overallMinutes",
              },
              {
                header: "Usage (hours)",
                key: "usageMinutes",
                render: (row) => formatMinutes(row.usageMinutes),
                sortKey: "usageMinutes",
              },
              {
                header: "Leftover (hours)",
                key: "leftoverMinutes",
                render: (row) => formatMinutes(row.leftoverMinutes),
                sortKey: "leftoverMinutes",
              },
              {
                header: "Pending approval (hours)",
                key: "pendingMinutes",
                render: (row) => formatMinutes(row.pendingMinutes),
                sortKey: "pendingMinutes",
              },
            ]}
            defaultPageSize={10}
            defaultSort={{ direction: "desc", key: "usageMinutes" }}
            getRowKey={(row) => row.labelId}
            pageSizeOptions={[10, 25, 50, 100]}
            rows={caregiverPtosOverall}
            showEmptyState={false}
            tableSx={attendanceTableSx}
          />

          {caregiverPtosBreakdown.length > 0 ? (
            <>
              <AttendanceSubheading>Breakdown</AttendanceSubheading>
              <EntityProfileDataTable
                columns={[
                  {
                    header: "Certification",
                    key: "certification",
                    render: (row) => formatCaregiverProfileValue(row.certification),
                    sortKey: "certification",
                  },
                  {
                    header: "Label",
                    key: "label",
                    render: (row) => row.ptoLabelText,
                    sortKey: "label",
                  },
                  {
                    header: "PTO Date",
                    key: "ptoDate",
                    render: (row) => formatDate(row.dateTime),
                    sortKey: "ptoDate",
                    sortValue: (row) => getSortValue(row.dateTime),
                  },
                  {
                    header: "PTO duration (hours)",
                    key: "durationMinutes",
                    render: (row) => formatMinutes(row.durationMinutes),
                    sortKey: "durationMinutes",
                  },
                  {
                    header: "PTO notes",
                    key: "notes",
                    render: (row) => (Array.isArray(row.notes) ? row.notes.join(", ") : ""),
                  },
                ]}
                defaultPageSize={10}
                getRowKey={(row) => row.caregiverPtoInstanceId}
                pageSizeOptions={[10, 25, 50, 100]}
                rows={caregiverPtosBreakdown}
                tableSx={attendanceTableSx}
              />
            </>
          ) : null}
        </AttendanceSectionCard>
      </VStack>
    );

  return (
    <>
      <YearControl currentYear={year} onChange={setYear} />
      {attendanceQuery.isFetching ? (
        <Progress isIndeterminate colorScheme="blue" h="3px" mb="12px" mt="-12px" opacity={0.75} />
      ) : null}
      {attendanceQuery.isError && attendance === undefined ? (
        <EntityProfileErrorState
          label="Could not load caregiver attendance."
          onRetry={() => void attendanceQuery.refetch()}
        />
      ) : (
        content
      )}
    </>
  );
}
